import React, { useEffect } from 'react';
import { useOpenDayData } from '../hooks/useOpenDayData';
import OpenDayBody from '../components/OpenDayBody';
import { formatOpenDayDateTime } from '../utils/dateFormatters';

const OpenDays = () => {
  // Detect language from URL pathname
  const initialLang = window.location.pathname.startsWith('/cy') ? 'cy' : 'en';
  
  // Get programme type from injected data (set by Squiz)
  const dataEl = document.getElementById('open-day-data');
  const initialData = dataEl ? JSON.parse(dataEl.textContent) : {};
  const programmeType = initialData.programmeType || 'U'; // Default to Undergrad
  
  const {
    lang,
    displayItems,
    topicsByOpenDay,
    loading,
    error,
    isSingleOpenDay,
    eventsByOpenDay,
    detailsLoadingId,
    detailsErrorId,
    openIds,
    searchByOpenDay,
    topicFilterByOpenDay,
    pageByOpenDay,
    handleAccordionClick,
    handleSearchChange,
    handleTopicFilterChange,
    handlePageChange,
    fetchEventsForOpenDay,
  } = useOpenDayData(initialLang, programmeType, []);

  // Handle single open day pre-fetching
  useEffect(() => {
    if (loading || error || !isSingleOpenDay) return;

    const item = displayItems[0];
    if (item && !eventsByOpenDay[item.id] && detailsLoadingId !== item.id) {
      fetchEventsForOpenDay(item.id);
    }
  }, [
    loading,
    error,
    isSingleOpenDay,
    displayItems,
    eventsByOpenDay,
    detailsLoadingId,
    fetchEventsForOpenDay,
  ]);

  if (loading) return <p>Loading…</p>;
  if (error) return <p style={{ color: 'red' }}>{error}</p>;

  const noEventsMessage = programmeType === 'P'
    ? (lang === 'cy' 
        ? 'Nid oes unrhyw Ddiwrnodau Agored ôl-raddedig ar gael ar hyn o bryd.'
        : 'There are currently no Postgraduate Open Days available.')
    : (lang === 'cy'
        ? 'Nid oes unrhyw Ddiwrnodau Agored ar gael ar hyn o bryd.'
        : 'There are currently no Open Days available.');

  return (
    <article className="article article--standard">
      <section id="content-container">
        {displayItems.length === 0 ? (
          <p className="alert">
            <strong>{noEventsMessage}</strong>
          </p>
        ) : (
          <div className={!isSingleOpenDay ? 'accordion accordion-accessible' : ''}>
            {displayItems.map((item) => {
              const isOpen = isSingleOpenDay || openIds.includes(item.id);
              const start = new Date(item.start_time);
              const end = new Date(item.end_time);
              const dateTimeLabel = formatOpenDayDateTime(start, end, lang);

              return (
                <div
                  key={item.id}
                  className={!isSingleOpenDay ? 'accordion-section' : 'single-open-day'}
                >
                  <div className="accordion-section-header">
                    <h2
                      className={`accordion-section-header-title ${
                        !isOpen && !isSingleOpenDay ? 'collapsed' : ''
                      }`}
                      onClick={() => !isSingleOpenDay && handleAccordionClick(item.id)}
                      style={{ cursor: isSingleOpenDay ? 'default' : 'pointer' }}
                    >
                      {!isSingleOpenDay && (
                        <svg className="icon icon-chevron-right" viewBox="0 0 19.75 32">
                          <path
                            d="M6.13,28.28L18.41,16,6.13,3.72,9.84,0l16,16-16,16Z"
                            transform="translate(-6.13 0)"
                          />
                        </svg>
                      )}
                      <span className="accordion-section-header-content">
                        <span className="accordion-section-header-text">
                          {item.description || 'No description'}
                        </span>
                        <br />
                        <small className="accordion-section-header-dates">{dateTimeLabel}</small>
                      </span>
                    </h2>
                  </div>

                  <div className={`accordion-section-body ${isOpen ? 'show' : 'collapse'}`}>
                    {isOpen && (
                      <OpenDayBody
                        item={item}
                        events={eventsByOpenDay[item.id] || []}
                        topics={topicsByOpenDay[item.id] || []}
                        searchValue={searchByOpenDay[item.id] || ''}
                        selectedTopicId={topicFilterByOpenDay[item.id] || 'all'}
                        currentPage={pageByOpenDay[item.id] || 1}
                        onSearchChange={(val) => handleSearchChange(item.id, val)}
                        onTopicFilterChange={(tid) => handleTopicFilterChange(item.id, tid)}
                        onPageChange={(p) => handlePageChange(item.id, p)}
                        isLoadingEvents={detailsLoadingId === item.id}
                        isErrorEvents={detailsErrorId === item.id}
                        lang={lang}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </article>
  );
};

export default OpenDays;
